<template>
  <div class="grading-view-container">
    <div class="top-bar">
      <div class="top-bar-content">
        <div class="left-section">
          <button
            @click="goBackToAssessments"
            class="back-button"
            title="Takaisin arvioitaviin töihin"
          >
            <BaseIcon
              name="arrow-left-circle"
              :size="28"
              class-name="back-arrow-icon"
              aria-label="Takaisin"
            />
          </button>
          <img
            src="/Teho_aly_logo.svg"
            alt="Teho-aly"
            class="logo"
            onerror="this.src='/Teho_aly_logo.png'"
          />
        </div>
        <div class="model-selector-container">
          <button
            @click="showModelSelector = true"
            class="model-button"
            :title="
              availableModels.find((m) => m.id === selectedModel)?.description
            "
          >
            <span class="model-name">
              {{
                availableModels.find((m) => m.id === selectedModel)?.name ||
                selectedModel
              }}
            </span>
            <span class="separator">|</span>
            <span class="image-status">
              {{ includeImages ? "kuvilla" : "ilman kuvia" }}
            </span>
          </button>
        </div>
        <UserSection :menuItems="userMenuItems" />
      </div>
    </div>

    <div class="sections-container">
      <draggable
        v-model="sections"
        item-key="id"
        handle=".section-header"
        @start="isDragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element: section }">
          <div :data-section-id="section.id">
            <div v-if="section.id === 'details'">
              <SectionWrapper
                :title="section.title"
                :is-collapsed="section.isCollapsed"
                :is-dragging="isDragging"
                @toggle="toggleSection(section.id)"
              >
                <AssignmentDetails
                  v-if="assessmentData"
                  :assignmentData="assessmentData"
                  :is-gallery-open="!!fullscreenImage"
                  :teacher-notes="teacherNotes"
                  :is-grading-started="isGradingStarted"
                  :is-peer-gallery-open="!!showPeerGallery"
                  @open-image-gallery="openImageGallery"
                  @close-image-gallery="closeImageGallery"
                  @open-assignment-gallery="openAssignmentGallery"
                  @update-teacher-notes="updateTeacherNotes"
                  @start-grading="onStartGrading"
                  @reset-grading="onResetGrading"
                  @update-gallery-link="updateGalleryLink"
                  @toggle-peer-gallery="togglePeerGallery"
                  @close-peer-gallery="closePeerGallery"
                  @assignment-confirmed="onAssignmentConfirmed"
                />
                <div v-else-if="fetchError" class="loading-error">
                  Tietojen lataus epäonnistui.
                </div>
                <div v-else class="loading-error">Ladataan...</div>
              </SectionWrapper>
            </div>

            <div v-else-if="section.id === 'gallery' && fullscreenImage">
              <div class="section-wrapper">
                <div
                  class="section-header"
                  :class="{ 'is-dragging': isDragging, 'grab-cursor': true }"
                >
                  <span class="grip-icon">⠿</span>
                  <h3>Opiskelijan kuvat</h3>
                  <button class="close-button" @click="closeImageGallery">
                    ✕
                  </button>
                </div>
                <div class="section-content">
                  <ImageViewer
                    :images="allImages"
                    :initialImage="fullscreenImage"
                    @close="closeImageGallery"
                    @image-changed="onStudentImageChanged"
                    :show-controls="false"
                  />
                </div>
              </div>
            </div>

            <div
              v-else-if="
                section.id === 'assignment-gallery' && assignmentGalleryImage
              "
            >
              <div class="section-wrapper">
                <div
                  class="section-header"
                  :class="{ 'is-dragging': isDragging, 'grab-cursor': true }"
                >
                  <span class="grip-icon">⠿</span>
                  <h3>Tehtävän kuvat</h3>
                  <button class="close-button" @click="closeAssignmentGallery">
                    ✕
                  </button>
                </div>
                <div class="section-content">
                  <ImageViewer
                    :images="allAssignmentImages"
                    :initialImage="assignmentGalleryImage"
                    @close="closeAssignmentGallery"
                    @image-changed="onAssignmentImageChanged"
                    :show-controls="false"
                  />
                </div>
              </div>
            </div>

            <div v-else-if="section.id === 'peer-gallery' && showPeerGallery">
              <div class="section-wrapper">
                <div
                  class="section-header"
                  :class="{ 'is-dragging': isDragging, 'grab-cursor': true }"
                >
                  <span class="grip-icon">⠿</span>
                  <h3>Kotitehtävägalleria</h3>
                  <button class="close-button" @click="closePeerGallery">
                    ✕
                  </button>
                </div>
                <div class="section-content peer-gallery-content">
                  <iframe
                    v-if="peerGalleryUrl"
                    :src="peerGalleryUrl"
                    class="peer-gallery-iframe"
                    frameborder="0"
                    allowfullscreen
                    title="Kotitehtävägalleria"
                  ></iframe>
                </div>
              </div>
            </div>

            <div v-else>
              <SectionWrapper
                :title="section.title"
                :is-collapsed="section.isCollapsed"
                :is-dragging="isDragging"
                :is-disabled="section.id === 'chat' && !isGradingStarted"
                :show-close-button="
                  section.id === 'system-prompt' ||
                  section.id === 'past-review-examples'
                "
                @toggle="toggleSection(section.id)"
                @close="closeSection(section.id)"
              >
                <ChatWindow
                  v-if="section.component === 'ChatWindow'"
                  :assessment-id="props.id"
                  :teacher-notes="teacherNotes"
                  :assignment-context="assignmentContext"
                  :is-initial-loading="isInitialReviewLoading"
                  :initial-message="initialReviewMessage"
                  :system-prompt="resolvedSystemPrompt"
                  :selected-model="selectedModel"
                  :is-loading="isChatLoading || isInitialReviewLoading"
                  :teacher-examples="resolvedTeacherExamples"
                  :student-images="allImages"
                  :include-images="includeImages"
                  :can-select-winner="isGradingStarted"
                  :selected-winner-id="selectedWinnerId"
                  @loading-changed="isChatLoading = $event"
                  @select-winner="selectWinner"
                />
                <SystemPrompt
                  v-else-if="section.component === 'SystemPrompt'"
                  ref="systemPromptRef"
                  :assessment-id="props.id"
                />
                <PastReviewExamples
                  v-else-if="section.component === 'PastReviewExamples'"
                  ref="pastReviewExamplesRef"
                  :assessment-id="props.id"
                />
                <FinalizationEditor
                  v-else-if="section.component === 'FinalizationEditor'"
                  :initial-content="editorSeedContent"
                  :disabled="!isFinalizationEnabled"
                  :is-submitting="isSubmitting"
                  @content-change="updateFinalizationContent"
                  @submit-review="submitFinalReview"
                />
                <component v-else :is="getComponent(section.component)" />
              </SectionWrapper>
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <!-- Model Selector Modal -->
    <div
      v-if="showModelSelector"
      class="modal-overlay"
      @click="showModelSelector = false"
    >
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Valitse AI-malli</h3>
          <label class="header-checkbox-label">
            <input
              type="checkbox"
              v-model="includeImages"
              class="image-checkbox"
            />
            Sisällytä kuvat
          </label>
          <button @click="showModelSelector = false" class="close-button">
            ×
          </button>
        </div>
        <div class="modal-body">
          <div class="model-list">
            <div
              v-for="model in availableModels"
              :key="model.id"
              class="model-option"
              :class="{ selected: model.id === selectedModel }"
              @click="
                selectedModel = model.id;
                showModelSelector = false;
              "
            >
              <div class="model-name">{{ model.name }}</div>
              <div v-if="model.description" class="model-description">
                {{ model.description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AssignmentDetails from "@/components/AssignmentDetails.vue";
import BaseIcon from "@/components/BaseIcon.vue";
import ChatWindow from "@/components/ChatWindow.vue";
import FinalizationEditor from "@/components/FinalizationEditor.vue";
import ImageViewer from "@/components/ImageViewer.vue";
import PastReviewExamples from "@/components/PastReviewExamples.vue";
import SectionWrapper from "@/components/SectionWrapper.vue";
import SystemPrompt from "@/components/SystemPrompt.vue";
import UserSection from "@/components/UserSection.vue";
import { useAuthStore } from "@/stores";
import { useAssessmentStore } from "@/stores/assessment";
import {
  AssessmentState,
  defaultProfileStates,
  SavedSection,
} from "@/stores/types";
import {
  computed,
  nextTick,
  onMounted,
  ref,
  shallowRef,
  watch,
  type Component,
} from "vue";
import { useRouter } from "vue-router";
import draggable from "vuedraggable";
import type { Message } from "../types/chat";

interface Props {
  id: string;
}

const props = defineProps<Props>();

// Assessment state computed property (Pinia-based)
const assessmentState = computed<AssessmentState>(() => {
  // Get from Pinia cache first for immediate rendering
  const cached = authStore.getCurrentProfileAssessmentStates[props.id];
  if (cached) {
    return cached;
  }

  // Fallback to default state
  return {
    ...defaultProfileStates.assessmentState,
  };
});

// Helper functions for assessment state operations
// TODO: Deprecated, do not use. Is not type safe!
const updateAssessmentState = (updates: Record<string, unknown>) => {
  authStore.updateAssessmentStateDebounced(props.id, updates);
};

// Helper functions for profile preferences
const updatePreference = (key: string, value: unknown) => {
  if (authStore.currentProfile) {
    const currentPreferences = authStore.currentProfile.state.preferences || {};
    const updatedProfile = {
      ...authStore.currentProfile,
      state: {
        ...authStore.currentProfile.state,
        preferences: {
          ...currentPreferences,
          [key]: value,
        },
      },
    };
    authStore.setProfile(updatedProfile);
  }
};

const resetProfileState = () => {
  if (authStore.currentProfile) {
    const resetProfile = {
      ...authStore.currentProfile,
      state: {},
    };
    authStore.setProfile(resetProfile);
  }
};

interface Section {
  id: string;
  title: string;
  component: string;
  isCollapsed: boolean;
  isDisabled: boolean;
}

interface ImageObject {
  url: string;
  title: string;
  fileType: string;
}

const assessmentStore = useAssessmentStore();
const authStore = useAuthStore();
const router = useRouter();
const assessmentData = computed(() => assessmentStore.assessmentData);

// Menu items for the UserSection component
const userMenuItems = computed(() => [
  {
    id: "system-prompt",
    label: sections.value.find((s) => s.id === "system-prompt")
      ? "Piilota järjestelmäkehote"
      : "Näytä järjestelmäkehote",
    action: toggleSystemPrompt,
  },
  {
    id: "past-review-examples",
    label: sections.value.find((s) => s.id === "past-review-examples")
      ? "Piilota aiemmat esimerkit"
      : "Näytä aiemmat esimerkit",
    action: togglePastReviewExamples,
  },
  {
    id: "reset-layout",
    label: "Palauta oletustilaan",
    action: () => void resetLayout(),
  },
]);

const fetchError = ref(false);
const isSubmitting = ref(false);

const sections = ref<Section[]>([]);
const isDragging = ref(false);

// Helper function to scroll to a newly added section
const scrollToNewSection = (sectionId: string, delay = 0) => {
  setTimeout(() => {
    void nextTick(() => {
      scrollToSection(sectionId);
    });
  }, delay);
};

// Utility function to calculate text statistics (same as in ChatWindow)
const calculateTextStats = (content: string) => {
  // If content has HTML tags, extract plain text
  let plainText: string;
  if (content.includes("<") && content.includes(">")) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    plainText = tempDiv.textContent || tempDiv.innerText || "";
  } else {
    // If it's markdown or plain text, use as-is for word counting
    // Remove markdown formatting for more accurate word count
    plainText = content
      .replace(/[#*_`~[\]()]/g, "") // Remove markdown syntax
      .replace(/![.*?]\(.*?\)/g, "") // Remove image syntax
      .replace(/[.*?]\(.*?\)/g, "") // Remove link syntax
      .trim();
  }

  // Calculate word count (split by whitespace and filter non-empty)
  const words = plainText
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const wordCount = words.length;

  // Calculate character counts
  const characterCount = plainText.length;
  const characterCountNoSpaces = plainText.replace(/\s/g, "").length;

  return {
    wordCount,
    characterCount,
    characterCountNoSpaces,
  };
};

const fullscreenImage = ref<ImageObject | null>(null);

// Winner selection state for Viimeistely section
const selectedWinnerId = ref<string | null>(null);
const selectedWinnerContent = ref<string>("");
const isFinalizationEnabled = computed(() => !!selectedWinnerId.value);
// Separate seed ref passed to the editor — only updated when a winner is
// selected or state is restored, never on every keystroke. This breaks the
// feedback loop that caused the cursor to jump to the end on every edit.
const editorSeedContent = ref<string>("");

// Flag to prevent circular API calls during state restoration
const isRestoringState = ref(false);

// Track if past review examples section should be shown
const showPastReviewExamples = ref(false);
const allImages = ref<ImageObject[]>([]);

// Teacher notes state
const teacherNotes = ref<string>("");

// System prompt ref
const systemPromptRef = ref<InstanceType<typeof SystemPrompt> | null>(null);
// Past review examples ref
const pastReviewExamplesRef = ref<InstanceType<
  typeof PastReviewExamples
> | null>(null);

// Prefetched values used when the panels are not mounted
const fetchedSystemPrompt = ref("");
const fetchedTeacherExamples = ref("");

const resolvedSystemPrompt = computed(() => {
  if (systemPromptRef.value) {
    return (systemPromptRef.value.getCurrentPrompt() as string) || "";
  }
  return (
    authStore.currentProfile?.state?.preferences?.systemPrompt ||
    fetchedSystemPrompt.value
  );
});

const resolvedTeacherExamples = computed(() => {
  if (pastReviewExamplesRef.value) {
    return (pastReviewExamplesRef.value.getCurrentExamples() as string) || "";
  }
  return (
    authStore.currentProfile?.state?.preferences?.teacherExamples ||
    fetchedTeacherExamples.value
  );
});

// Grading state
const isGradingStarted = ref(false);
const isInitialReviewLoading = ref(false);
const initialReviewMessage = ref<Message | null>(null);
const isChatLoading = ref(false);

// Model selection state
interface ModelInfo {
  id: string;
  name: string;
  description?: string;
}

const availableModels = ref<ModelInfo[]>([]);
const selectedModel = ref<string>("gpt-4o-mini");
const showModelSelector = ref(false);
const includeImages = ref<boolean>(true);

// Save grading state whenever it changes (scoped to assessment)
watch(isGradingStarted, (newValue) => {
  if (!isRestoringState.value) {
    updateAssessmentState({ isGradingStarted: newValue });
  }
});

// Save selected model and image inclusion settings to profile preferences
watch(selectedModel, (newValue) => {
  updatePreference("selectedModel", newValue);
});

watch(includeImages, (newValue) => {
  updatePreference("includeImages", newValue);
});

// Save teacher notes with automatic debounced backend sync
watch(teacherNotes, (newValue) => {
  if (!isRestoringState.value) {
    updateAssessmentState({ teacherNotes: newValue });
  }
});

// Save finalization state when any component changes
watch(
  [selectedWinnerId, selectedWinnerContent],
  () => {
    if (!isRestoringState.value) {
      const finalizationState = {
        selectedWinnerId: selectedWinnerId.value,
        selectedWinnerContent: selectedWinnerContent.value,
      };
      updateAssessmentState({ finalizationState });
    }
  },
  { deep: true },
);

// Save past review examples visibility state
watch(showPastReviewExamples, (newValue) => {
  if (!isRestoringState.value) {
    updateAssessmentState({ showPastReviewExamples: newValue });
  }
});

// Save gallery states when they change
watch(fullscreenImage, (newValue) => {
  if (!isRestoringState.value) {
    const galleryState = {
      isOpen: !!newValue,
      currentImageUrl: newValue?.url || null,
    };
    updateAssessmentState({ galleryState });
  }
});

// Separate state for assignment images gallery
const assignmentGalleryImage = ref<ImageObject | null>(null);
watch(assignmentGalleryImage, (newValue) => {
  if (!isRestoringState.value) {
    const assignmentGalleryState = {
      isOpen: !!newValue,
      currentImageUrl: newValue?.url || null,
    };
    updateAssessmentState({ assignmentGalleryState });
  }
});

// Peer gallery state
const showPeerGallery = ref(false);
const peerGalleryUrl = ref<string | null>(null);
// Save peer gallery state when it changes
watch([showPeerGallery, peerGalleryUrl], () => {
  if (!isRestoringState.value) {
    const peerGalleryState = {
      isOpen: showPeerGallery.value,
      galleryUrl: peerGalleryUrl.value,
    };
    updateAssessmentState({ peerGalleryState });
  }
});

// Save layout when sections change (collapsed state, order, etc.)
watch(
  sections,
  () => {
    if (!isRestoringState.value) {
      const layoutData = sections.value.map((section) => ({
        id: section.id,
        isCollapsed: section.isCollapsed,
      }));
      updateAssessmentState({ gradingViewLayout: layoutData });
    }
  },
  { deep: true },
);

const fetchSystemData = async () => {
  const apiBaseUrl =
    (process.env.VUE_APP_API_BASE_URL as string) || "http://localhost:3000/api";
  try {
    const [promptRes, examplesRes] = await Promise.all([
      fetch(`${apiBaseUrl}/system-prompt/${props.id}`, {
        credentials: "include",
      }),
      fetch(`${apiBaseUrl}/teacher-examples/${props.id}`, {
        credentials: "include",
      }),
    ]);
    if (promptRes.ok) {
      const data = (await promptRes.json()) as { systemPrompt?: string };
      fetchedSystemPrompt.value = data.systemPrompt || "";
    }
    if (examplesRes.ok) {
      const data = (await examplesRes.json()) as { teacherExamples?: string };
      fetchedTeacherExamples.value = data.teacherExamples || "";
    }
  } catch (err) {
    console.warn("Failed to prefetch system prompt/examples:", err);
  }
};

// Fetch available models from backend
const fetchAvailableModels = async () => {
  try {
    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) ||
      "http://localhost:3000/api";
    const response = await fetch(`${apiBaseUrl}/models`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch models");
    }

    const data = (await response.json()) as { models: ModelInfo[] };
    availableModels.value = data.models;
  } catch (error) {
    console.error("Error fetching models:", error);
    // Fallback models if API fails
    availableModels.value = [
      {
        id: "gpt-4o-mini",
        name: "GPT-4o Mini",
        description: "Fast and cost-effective",
      },
      { id: "gpt-4o", name: "GPT-4o", description: "Most capable model" },
    ];
  }
};

// Assignment context for AI
const assignmentContext = computed(() => {
  if (!assessmentData.value) return undefined;
  return {
    title:
      assessmentData.value.learningUnit?.title ||
      assessmentData.value.section?.title,
    description:
      assessmentData.value.assignmentDetails?.tehtävänanto ||
      assessmentData.value.section?.description,
    criteria: assessmentData.value.assignmentDetails?.arviointiperusteet || [],
  };
});

const allAssignmentImages = ref<ImageObject[]>([]);

// Use shallowRef for components to avoid deep reactivity overhead
const componentMap = shallowRef<Record<string, Component>>({
  AssignmentDetails,
  ChatWindow,
  FinalizationEditor,
  SystemPrompt,
});

const getComponent = (componentName: string): Component | undefined => {
  return componentMap.value[componentName];
};

const getSectionDefaults = (id: string): Section => {
  const defaults = {
    details: {
      title: "Tehtävän tiedot",
      component: "AssignmentDetails",
    },
    chat: {
      title: "Arviointi",
      component: "ChatWindow",
      isCollapsed: true,
      isDisabled: true,
    },
    viimeistely: {
      title: "Viimeistely",
      component: "FinalizationEditor",
      isCollapsed: true,
      isDisabled: true,
    },
    gallery: {
      title: "Opiskelijan kuvat",
      component: "ImageViewer",
    },
    "assignment-gallery": {
      title: "Tehtävän kuvat",
      component: "ImageViewer",
    },
    "peer-gallery": {
      title: "Kotitehtävägalleria",
      component: "PeerGallery",
    },
    "system-prompt": {
      title: "Järjestelmäkehote",
      component: "SystemPrompt",
    },
    "past-review-examples": {
      title: "Aiemmat arviointiesimerkit",
      component: "PastReviewExamples",
    },
  };
  return {
    ...{
      id,
      title: id,
      component: "Unknown",
      isCollapsed: false,
      isDisabled: false,
    },
    ...defaults[id as keyof typeof defaults],
  };
};

const initializeSections = () => {
  try {
    const savedLayout = assessmentState.value.gradingViewLayout;

    if (savedLayout && Array.isArray(savedLayout) && savedLayout.length > 0) {
      // Reconstruct full section objects from saved data
      sections.value = savedLayout.map((savedSection: SavedSection) => {
        const defaults = getSectionDefaults(savedSection.id);
        return {
          ...defaults,
          ...savedSection,
        };
      });
    } else {
      sections.value = ["details", "chat"].map((sectionId) => {
        const defaults = getSectionDefaults(sectionId);
        return {
          ...defaults,
        };
      });
    }
  } catch (e) {
    console.error("Failed to restore layout from profile state", e);
  }
};

const saveLayout = () => {
  // This function is now mostly redundant since individual watchers
  // handle automatic saving of all state changes.
  // Keeping it for backwards compatibility and explicit save calls.
  console.log(
    "💾 Layout save triggered - automatic watchers handle most saving now",
  );
};

const toggleSection = (id: string) => {
  // SystemPrompt is not collapsible, only closable
  if (id === "system-prompt") {
    return;
  }

  const section = sections.value.find((s) => s.id === id);
  if (section) {
    const wasCollapsed = section.isCollapsed;
    section.isCollapsed = !section.isCollapsed;
    saveLayout();

    // If section was just expanded (opened), scroll to it
    if (wasCollapsed && !section.isCollapsed) {
      void nextTick(() => {
        scrollToSection(id);
      });
    }
  }
};

const onDragEnd = () => {
  isDragging.value = false;
  saveLayout();
};

const restoreGalleryState = () => {
  if (!assessmentData.value) return;

  try {
    isRestoringState.value = true;
    // Restore student gallery state
    const galleryState = assessmentState.value.galleryState;
    console.log("Restoring gallery state:", galleryState);
    if (galleryState) {
      console.log("Parsed gallery state:", galleryState);
      if (galleryState?.isOpen && galleryState?.currentImageUrl) {
        // Find the image in assessment data
        const images =
          (assessmentData.value?.answers?.[0]?.answerData
            ?.downloads as ImageObject[]) || [];
        console.log("Available images:", images.length);
        const image = images.find(
          (img) => img.url === galleryState?.currentImageUrl,
        );
        console.log("Found matching image:", !!image);
        if (image) {
          allImages.value = images;
          fullscreenImage.value = image;
          console.log("Gallery state restored successfully");
        }
      }
    }

    // Restore assignment gallery state
    const { galleryState: assignmentGalleryState } = assessmentState.value;
    if (assignmentGalleryState) {
      if (
        assignmentGalleryState.isOpen &&
        assignmentGalleryState.currentImageUrl
      ) {
        // Find the image in assignment images
        const assignmentImages =
          assessmentData.value?.assignmentDetails?.kuvat || [];
        if (assignmentImages.includes(assignmentGalleryState.currentImageUrl)) {
          // Create image object and all assignment images array
          const imageObject = {
            url: assignmentGalleryState.currentImageUrl,
            title: "Tehtävän kuva",
            fileType: "image/jpeg",
          };

          const allAssignmentImagesArray = assignmentImages.map(
            (url: string, index: number) => ({
              url: url,
              title: `Tehtävän kuva ${index + 1}`,
              fileType: "image/jpeg",
            }),
          );

          allAssignmentImages.value = allAssignmentImagesArray;
          assignmentGalleryImage.value = imageObject;
          console.log("Assignment gallery state restored successfully");
        }
      }
    }

    // Restore peer gallery state
    const peerGalleryState = assessmentState.value.peerGalleryState;
    if (peerGalleryState?.isOpen && peerGalleryState?.galleryUrl) {
      showPeerGallery.value = true;
      peerGalleryUrl.value = peerGalleryState.galleryUrl;

      // Add peer gallery section if it doesn't exist
      const peerGalleryExists = sections.value.find(
        (s) => s.id === "peer-gallery",
      );
      if (!peerGalleryExists) {
        const detailsIndex = sections.value.findIndex(
          (s) => s.id === "details",
        );
        const insertIndex = detailsIndex !== -1 ? detailsIndex + 1 : 1;

        sections.value.splice(
          insertIndex,
          0,
          getSectionDefaults("peer-gallery"),
        );
        scrollToNewSection("peer-gallery", 150);
      }
      console.log("Peer gallery state restored successfully");
    }
  } catch (e) {
    console.error("Failed to restore gallery state:", e);
    updateAssessmentState({ galleryState: undefined });
    updateAssessmentState({ assignmentGalleryState: undefined });
    updateAssessmentState({ peerGalleryState: undefined });
  } finally {
    isRestoringState.value = false;
  }
};

const openImageGallery = (image: ImageObject) => {
  allImages.value =
    (assessmentData.value?.answers?.[0]?.answerData
      ?.downloads as ImageObject[]) || [];
  fullscreenImage.value = image;

  // Add gallery section if it doesn't exist
  const galleryExists = sections.value.find((s) => s.id === "gallery");
  if (!galleryExists) {
    // Find the index of the details section and insert gallery right after it
    const detailsIndex = sections.value.findIndex((s) => s.id === "details");
    const insertIndex = detailsIndex !== -1 ? detailsIndex + 1 : 1;

    sections.value.splice(insertIndex, 0, getSectionDefaults("gallery"));
    scrollToNewSection("gallery", 150);
  }

  // Save the layout and gallery state immediately
  saveLayout();
};

const openAssignmentGallery = (image: ImageObject, images: ImageObject[]) => {
  allAssignmentImages.value = images;
  assignmentGalleryImage.value = image;

  // Add assignment gallery section if it doesn't exist
  const assignmentGalleryExists = sections.value.find(
    (s) => s.id === "assignment-gallery",
  );
  if (!assignmentGalleryExists) {
    // Find the index to insert assignment gallery (after details but before regular gallery if it exists)
    const detailsIndex = sections.value.findIndex((s) => s.id === "details");
    const galleryIndex = sections.value.findIndex((s) => s.id === "gallery");

    let insertIndex;
    if (galleryIndex !== -1) {
      // Insert before regular gallery
      insertIndex = galleryIndex;
    } else {
      // Insert after details
      insertIndex = detailsIndex !== -1 ? detailsIndex + 1 : 1;
    }

    sections.value.splice(
      insertIndex,
      0,
      getSectionDefaults("assignment-gallery"),
    );
    scrollToNewSection("assignment-gallery", 150);
  }

  // Save the layout immediately
  saveLayout();
};

const closeImageGallery = () => {
  fullscreenImage.value = null;

  // Remove gallery section
  sections.value = sections.value.filter((s) => s.id !== "gallery");

  // Clear gallery state from profile state
  updateAssessmentState({ galleryState: undefined });
  saveLayout();
};

const closeAssignmentGallery = () => {
  assignmentGalleryImage.value = null;

  // Remove assignment gallery section
  sections.value = sections.value.filter((s) => s.id !== "assignment-gallery");

  // Save layout
  saveLayout();
};

const togglePeerGallery = (galleryUrl: string) => {
  if (showPeerGallery.value && peerGalleryUrl.value) {
    closePeerGallery();
  } else {
    openPeerGallery(galleryUrl);
  }
};

const openPeerGallery = (galleryUrl: string) => {
  peerGalleryUrl.value = galleryUrl;
  showPeerGallery.value = true;

  // Add peer gallery section if it doesn't exist
  const peerGalleryExists = sections.value.find((s) => s.id === "peer-gallery");
  if (!peerGalleryExists) {
    // Find the index of the details section and insert peer gallery right after it
    const detailsIndex = sections.value.findIndex((s) => s.id === "details");
    const insertIndex = detailsIndex !== -1 ? detailsIndex + 1 : 1;

    sections.value.splice(insertIndex, 0, getSectionDefaults("peer-gallery"));
    scrollToNewSection("peer-gallery", 150);
  }

  // Save the layout immediately
  saveLayout();
};

const closePeerGallery = () => {
  showPeerGallery.value = false;
  peerGalleryUrl.value = null;

  // Remove peer gallery section
  sections.value = sections.value.filter((s) => s.id !== "peer-gallery");

  // Clear peer gallery state from profile state
  updateAssessmentState({ peerGalleryState: undefined });
  saveLayout();
};

const onStudentImageChanged = (image: ImageObject) => {
  fullscreenImage.value = image;
  saveLayout(); // Save the new current image
};

const onAssignmentImageChanged = (image: ImageObject) => {
  assignmentGalleryImage.value = image;
  saveLayout(); // Save the new current image
};

const onStartGrading = async (notes: string) => {
  isGradingStarted.value = true;
  isInitialReviewLoading.value = true;

  // Auto-expand the Arviointi section when grading starts
  const chatSection = sections.value.find((s) => s.id === "chat");
  if (chatSection && chatSection.isCollapsed) {
    chatSection.isCollapsed = false;
    saveLayout();
  }

  // Scroll to the Arviointi section and focus the chat input
  void nextTick(() => {
    scrollToSection("chat");
    // Focus the chat input after a short delay to ensure it's rendered
    setTimeout(() => {
      const chatInput = document.querySelector(
        ".chat-window .message-input textarea",
      ) as HTMLTextAreaElement;
      if (chatInput) {
        chatInput.focus();
      }
    }, 300);
  });

  try {
    // Get assessment ID from route props
    const assessmentId = props.id;

    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) || "http://localhost:3000";
    const response = await fetch(
      `${apiBaseUrl}/assessment_responses/${assessmentId}/review`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          message:
            "Aloita arviointi. Anna yleinen arvio opiskelijan työstä ja ehdota keskusteluaiheita jatkoarviointia varten.",
          teacherNotes: notes,
          assignmentContext: assignmentContext.value,
          systemPrompt: resolvedSystemPrompt.value,
          model: selectedModel.value,
          teacherExamples: resolvedTeacherExamples.value,
          includeImages: includeImages.value,
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Alkuarvioinnin lataus epäonnistui.");
    }

    const apiResponse = (await response.json()) as {
      id: string;
      sender: string;
      content: string;
      timestamp: string;
    };
    initialReviewMessage.value = {
      ...apiResponse,
      timestamp: new Date(apiResponse.timestamp),
      stats: calculateTextStats(apiResponse.content),
      originalMarkdown: apiResponse.content, // Store original markdown
    } as Message;
  } catch (error) {
    console.error("Error getting initial review:", error);
    initialReviewMessage.value = {
      id: `error-${Date.now()}`,
      sender: "ai",
      content:
        "<p>Pahoittelut, alkuarvioinnin lataus epäonnistui. Voit kuitenkin aloittaa keskustelun normaalisti.</p>",
      timestamp: new Date(),
    };
  } finally {
    isInitialReviewLoading.value = false;
  }
};

const onResetGrading = () => {
  // Reset grading state
  isGradingStarted.value = false;
  isInitialReviewLoading.value = false;
  initialReviewMessage.value = null;

  // We do not clear teacher's notes so, that the teacher can use the previous notes as the basis for new notes and doesn't need to start from scratch

  // Clear scoped assessment state for grading-related data
  // IMPORTANT: DO NOT CLEAR THE TEACHERS NOTES HERE!!!
  // We just want to allow editing them, but keep the old data for the sake of convenience. Likely the teacher only wants to do some minor edit or addition to them instead of a complete rewrite.
  authStore.updateAssessmentStateDebounced(props.id, {
    isGradingStarted: false,
    chatMessages: [],
    finalizationState: {
      selectedWinnerId: "",
      selectedWinnerContent: "",
    },
    showPastReviewExamples: false,
  });

  // Collapse and disable the Arviointi section
  const chatSection = sections.value.find((s) => s.id === "chat");
  if (chatSection) {
    chatSection.isCollapsed = true;
    chatSection.isDisabled = true;
  }

  // Save the layout changes
  saveLayout();

  // Reset winner selection and remove Viimeistely section
  selectedWinnerId.value = null;
  selectedWinnerContent.value = "";

  // Reset past review examples state
  showPastReviewExamples.value = false;

  // Remove Viimeistely, past review examples, and peer gallery sections if they exist
  sections.value = sections.value.filter(
    (s) =>
      s.id !== "viimeistely" &&
      s.id !== "past-review-examples" &&
      s.id !== "peer-gallery",
  );

  // Reset peer gallery state
  showPeerGallery.value = false;
  peerGalleryUrl.value = null;
};

const updateTeacherNotes = (notes: string) => {
  teacherNotes.value = notes;
  // Notes are automatically saved via watcher with debouncing
};

const updateGalleryLink = (newLink: string | null) => {
  // Update the assessmentData with the new gallery link
  if (assessmentData.value?.assignmentDetails) {
    assessmentData.value.assignmentDetails.peerGalleryDriveLink = newLink;
  }
};

const onAssignmentConfirmed = (details: {
  id: number;
  isConfirmed: boolean;
  tehtävänanto: string;
  arviointiperusteet: string[];
  kuvat: string[];
  peerGalleryDriveLink: null;
}) => {
  if (assessmentData.value) {
    assessmentData.value.assignmentDetails = details;
  }
};

const restoreTeacherNotes = () => {
  try {
    isRestoringState.value = true;
    const savedNotes = assessmentState.value.teacherNotes;
    if (savedNotes) {
      teacherNotes.value = savedNotes;
    }
  } catch (e) {
    console.error("Failed to restore teacher notes:", e);
  } finally {
    isRestoringState.value = false;
  }
};

const restoreGradingState = () => {
  try {
    isRestoringState.value = true;
    const savedState = assessmentState.value.isGradingStarted;
    if (savedState !== null) {
      isGradingStarted.value = savedState;
    }
  } catch (e) {
    console.error("Failed to restore grading state:", e);
    updateAssessmentState({ isGradingStarted: undefined });
  } finally {
    isRestoringState.value = false;
  }
};

const restoreFinalizationState = () => {
  try {
    isRestoringState.value = true;
    const savedState = assessmentState.value.finalizationState;
    console.log(
      "🔄 Restoring finalization state from profile state:",
      savedState,
    );
    if (savedState) {
      console.log("🔄 Parsed finalization state:", savedState);
      selectedWinnerId.value = savedState.selectedWinnerId;
      selectedWinnerContent.value = savedState.selectedWinnerContent;
      editorSeedContent.value = savedState.selectedWinnerContent;
      console.log("🔄 Applied finalization state:", {
        selectedWinnerId: selectedWinnerId.value,
        selectedWinnerContent: selectedWinnerContent.value,
        isFinalizationEnabled: isFinalizationEnabled.value,
      });

      // If finalization was enabled, ensure the Viimeistely section exists
      if (savedState?.selectedWinnerId) {
        const viimeistelyExists = sections.value.find(
          (s) => s.id === "viimeistely",
        );
        if (!viimeistelyExists) {
          // Find the index of the chat section and insert Viimeistely right after it
          const chatIndex = sections.value.findIndex((s) => s.id === "chat");
          const insertIndex =
            chatIndex !== -1 ? chatIndex + 1 : sections.value.length;

          sections.value.splice(insertIndex, 0, {
            id: "viimeistely",
            title: "Viimeistely",
            component: "FinalizationEditor",
            isCollapsed: false,
            isDisabled: false,
          });
        }
      }
    }
  } catch (e) {
    console.error("Failed to restore finalization state:", e);
    updateAssessmentState({ finalizationState: undefined });
  } finally {
    isRestoringState.value = false;
  }
};

const restorePastReviewExamplesState = () => {
  try {
    isRestoringState.value = true;
    const savedState = assessmentState.value.showPastReviewExamples;
    if (savedState !== null) {
      showPastReviewExamples.value = savedState;

      // If past review examples should be shown, ensure the section exists and is not filtered out
      if (showPastReviewExamples.value) {
        const examplesExists = sections.value.find(
          (s) => s.id === "past-review-examples",
        );
        if (!examplesExists) {
          // Add the section at the beginning
          const pastReviewExamplesSection = getSectionDefaults(
            "past-review-examples",
          );
          sections.value.unshift(pastReviewExamplesSection);
        }
      } else {
        // Remove the section if it shouldn't be shown
        sections.value = sections.value.filter(
          (s) => s.id !== "past-review-examples",
        );
      }
    }
  } catch (e) {
    console.error("Failed to restore past review examples state:", e);
    updateAssessmentState({ showPastReviewExamples: undefined });
  } finally {
    isRestoringState.value = false;
  }
};

// Winner selection for Viimeistely section
const selectWinner = (messageId: string, content: string) => {
  console.log("🏆 selectWinner called:", {
    messageId,
    contentLength: content.length,
  });

  // Check if we're changing selection and finalization is in progress
  const isChangingSelection =
    selectedWinnerId.value && selectedWinnerId.value !== messageId;
  const hasFinalizationContent =
    selectedWinnerContent.value &&
    selectedWinnerContent.value.trim() !== content.trim();

  if (isChangingSelection && hasFinalizationContent) {
    const confirmed = window.confirm(
      "Olet vaihtamassa valittua viestiä viimeistelyyn. Tämä toiminto poistaa nykyiset viimeistelyn muutokset.\n\n" +
        "Haluatko varmasti jatkaa? Nykyinen viimeisteltytyösi menetetään.",
    );

    if (!confirmed) {
      console.log("🚫 Winner selection change cancelled by user");
      return;
    }

    console.log(
      "✅ User confirmed winner selection change, clearing finalization data",
    );
  }

  // Update winner selection
  selectedWinnerId.value = messageId;
  selectedWinnerContent.value = content;
  editorSeedContent.value = content;
  console.log("🏆 Winner state updated:", {
    selectedWinnerId: selectedWinnerId.value,
    selectedWinnerContentLength: selectedWinnerContent.value.length,
    isFinalizationEnabled: isFinalizationEnabled.value,
  });

  // Add Viimeistely section if it doesn't exist
  const viimeistelyExists = sections.value.find((s) => s.id === "viimeistely");
  if (!viimeistelyExists) {
    // Find the index of the chat section and insert Viimeistely right after it
    const chatIndex = sections.value.findIndex((s) => s.id === "chat");
    const insertIndex =
      chatIndex !== -1 ? chatIndex + 1 : sections.value.length;

    sections.value.splice(insertIndex, 0, {
      id: "viimeistely",
      title: "Viimeistely",
      component: "FinalizationEditor",
      isCollapsed: false,
      isDisabled: false,
    });

    saveLayout();

    // Scroll to the new section
    void nextTick(() => {
      scrollToSection("viimeistely");
    });
  }
};

// Handle content changes in FinalizationEditor
const updateFinalizationContent = (content: string) => {
  console.log("📝 FinalizationEditor content changed:", {
    contentLength: content.length,
  });
  selectedWinnerContent.value = content;
  // Save layout to persist the content change
  saveLayout();
};

// Handle final review submission
const submitFinalReview = async (content: string) => {
  // Prevent double submission
  if (isSubmitting.value) {
    console.log("Submission already in progress, ignoring duplicate request");
    return;
  }

  isSubmitting.value = true;

  try {
    console.log("Submitting final review:", content.substring(0, 100) + "...");

    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) || "http://localhost:3000";

    const response = await fetch(
      `${apiBaseUrl}/assessment_responses/${props.id}/submit`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ content }),
      },
    );

    if (!response.ok) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const errorData = await response.json();

      // Handle specific error cases
      if (response.status === 409) {
        // Already submitted - redirect without showing error
        console.log("Assessment already submitted, redirecting to list");
        await router.push({
          path: "/assessments",
          query: { submitted: "true" } as Record<string, string>,
        });
        return;
      }

      if (response.status === 502) {
        // LearnWorlds API failure - show error with retry option
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const errorMsg = errorData.details
          ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            `LearnWorlds API -virhe: ${errorData.details as string}`
          : "Palvelimen virhe lähetettäessä arviointia LearnWorlds-järjestelmään.";

        if (
          confirm(
            `${errorMsg}\n\nHaluatko yrittää uudelleen?\n\nKlikkaa OK yrittääksesi uudelleen tai Cancel peruuttaaksesi.`,
          )
        ) {
          isSubmitting.value = false;
          // Retry by calling the function again
          await submitFinalReview(content);
          return;
        } else {
          isSubmitting.value = false;
          return;
        }
      }

      // Other errors
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      throw new Error(errorData.error || "Failed to submit final review");
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = await response.json();
    console.log("Final review submitted successfully:", result);

    // Navigate back to assessments list with success state
    // The backend automatically excludes completed assessments from future queries
    await router.push({
      path: "/assessments",
      query: { submitted: "true" } as Record<string, string>,
    });
  } catch (error) {
    console.error("Failed to submit final review:", error);
    alert(
      `Virhe arvioinnin lähettämisessä: ${error instanceof Error ? error.message : "Tuntematon virhe"}\n\nYritä uudelleen.`,
    );
  } finally {
    isSubmitting.value = false;
  }
};

const toggleSystemPrompt = () => {
  const existingSection = sections.value.find((s) => s.id === "system-prompt");

  if (existingSection) {
    // Remove system prompt section
    sections.value = sections.value.filter((s) => s.id !== "system-prompt");
  } else {
    // Add system prompt section
    const systemPromptSection = {
      id: "system-prompt",
      title: "Järjestelmäkehote",
      component: "SystemPrompt",
      isCollapsed: false,
      isDisabled: false,
    };
    sections.value.unshift(systemPromptSection);
    scrollToNewSection("system-prompt");
  }

  saveLayout();
};

const togglePastReviewExamples = () => {
  // Toggle the state variable
  showPastReviewExamples.value = !showPastReviewExamples.value;

  if (showPastReviewExamples.value) {
    // Add past review examples section
    const pastReviewExamplesSection = {
      id: "past-review-examples",
      title: "Aiemmat arviointiesimerkit",
      component: "PastReviewExamples",
      isCollapsed: false,
      isDisabled: false,
    };
    // Add after system prompt if it exists, otherwise at the beginning
    const systemPromptIndex = sections.value.findIndex(
      (s) => s.id === "system-prompt",
    );
    if (systemPromptIndex !== -1) {
      sections.value.splice(
        systemPromptIndex + 1,
        0,
        pastReviewExamplesSection,
      );
    } else {
      sections.value.unshift(pastReviewExamplesSection);
    }
    scrollToNewSection("past-review-examples");
  } else {
    // Remove past review examples section
    sections.value = sections.value.filter(
      (s) => s.id !== "past-review-examples",
    );
  }

  saveLayout();
};

const closeSection = (sectionId: string) => {
  // Remove section when close button is clicked
  sections.value = sections.value.filter((s) => s.id !== sectionId);

  // Update state variables when sections are closed
  if (sectionId === "past-review-examples") {
    showPastReviewExamples.value = false;
  }

  saveLayout();
};

const scrollToSection = (sectionId: string) => {
  // Find the section element and scroll to it
  const sectionElement = document.querySelector(
    `[data-section-id="${sectionId}"]`,
  );
  if (sectionElement) {
    sectionElement.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};

const resetLayout = () => {
  // Clear all saved profile state data
  resetProfileState();

  // Reset state to defaults
  teacherNotes.value = "";
  fullscreenImage.value = null;
  assignmentGalleryImage.value = null;
  isGradingStarted.value = false;
  initialReviewMessage.value = null;

  // Reset system prompt to default if component is mounted
  if (systemPromptRef.value) {
    systemPromptRef.value.resetSystemPrompt();
  }
  // Reset past review examples to default if component is mounted
  if (pastReviewExamplesRef.value) {
    pastReviewExamplesRef.value.resetExamples();
  }

  // Reinitialize sections to default layout
  initializeSections();
};

onMounted(async () => {
  // Load assessment state from API to populate Pinia cache first
  // This MUST complete before restoration functions run
  await authStore.getAssessmentState(props.id);

  initializeSections();
  restoreTeacherNotes();
  restoreGradingState();
  restoreFinalizationState();
  restorePastReviewExamplesState();

  // Load saved preferences from profile state
  try {
    const preferences = authStore.currentProfile?.state?.preferences;

    if (preferences?.selectedModel) {
      selectedModel.value = preferences.selectedModel;
    }

    if (preferences?.includeImages !== undefined) {
      includeImages.value = preferences.includeImages;
    }
  } catch (err) {
    console.warn("Failed to load preferences from profile state:", err);
  }

  // Fetch available models and system data
  void fetchAvailableModels();
  void fetchSystemData();

  // Always fetch assessment data for the current assessment ID
  try {
    await assessmentStore.fetchAssessmentData(props.id);
    restoreGalleryState();
  } catch {
    fetchError.value = true;
  }
});

const goBackToAssessments = async () => {
  await router.push("/assessments");
};
</script>

<style>
/* Global styles to improve drag-and-drop feel */
body {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  background-color: #f0f2f5;
}
</style>

<style scoped>
.grading-view-container {
  padding-top: 60px; /* Space for the fixed top bar */
}

.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background-color: var(--color-bg-primary);
  border-bottom: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-sm);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding: 0 var(--space-md);
}

.top-bar-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.2rem;
  gap: var(--space-md);
  color: var(--color-text-primary);
  line-height: 1.2;
}

.left-section {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-shrink: 0;
}

.logo {
  height: 32px;
  width: auto;
  max-width: 100px;
}

.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-xs);
  background: transparent;
  border: none;
  border-radius: 100%;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.back-button:hover {
  background-color: var(--color-bg-tertiary);
  transform: scale(1.15);
}

.back-button .back-arrow-icon {
  transition: all 0.2s ease-in-out;
  color: var(--color-primary);
}

.back-button:hover .back-arrow-icon {
  opacity: 1;
  color: var(--color-primary);
}

.model-selector-container {
  display: flex;
  align-items: center;
}

.model-selector-container {
  margin: 0 1rem;
}

.model-button {
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--color-border-medium);
  background: var(--color-bg-primary);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary);
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  line-height: 1.2;
}

/* Responsive: stack vertically on narrow screens */
@media (max-width: 600px) {
  .top-bar-content {
    padding: 0 var(--space-sm);
  }

  .model-button {
    flex-direction: column;
    gap: 0.25rem;
    min-width: 120px;
    padding: var(--space-xs) var(--space-sm);
  }

  .separator {
    display: none;
  }
}

.model-button:hover {
  background: var(--color-bg-tertiary);
  border-color: var(--color-border-dark);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.model-name {
  font-weight: 600;
  font-size: 0.875rem;
}

.separator {
  color: var(--color-text-light);
  font-weight: 300;
  opacity: 0.6;
}

.image-status {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  font-weight: 400;
  opacity: 0.8;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  gap: 1rem;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.header-checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #555;
  cursor: pointer;
  user-select: none;
}

.modal-header .close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.25rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.modal-header .close-button:hover {
  background-color: #e9ecef;
  color: #333;
}

.modal-body {
  padding: 1rem;
  max-height: 60vh;
  overflow-y: auto;
}

.model-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.model-option {
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.model-option:hover {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.model-option.selected {
  border-color: #007bff;
  background-color: #e3f2fd;
}

.model-name {
  font-weight: 600;
  color: #333;
}

.model-description {
  font-size: 0.9rem;
  color: #666;
}

.image-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #007bff;
  cursor: pointer;
}

.sections-container {
  max-width: 1200px;
  margin: 1rem auto;
}

/* Apply scroll margin to the first section to account for fixed top bar */
.sections-container > div:first-child > div {
  scroll-margin-top: 80px; /* 60px for top bar + 20px breathing room */
}

.loading-error {
  padding: 2rem;
  text-align: center;
  color: #888;
}

.section-wrapper {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 1rem;
  background-color: #fff;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  user-select: none;
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.section-header.grab-cursor {
  cursor: grab;
}

.section-header:active {
  cursor: grabbing;
}

.section-header.is-dragging {
  background-color: #e9ecef;
}

.grip-icon {
  margin-right: 0.75rem;
  color: #888;
}

.section-header h3 {
  flex-grow: 1;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 2px;
}

.close-button:hover {
  color: #333;
  background-color: #f0f0f0;
}

/* Peer gallery iframe styles */
.peer-gallery-content {
  padding: 0;
}

.peer-gallery-iframe {
  width: 100%;
  height: 500px;
  border: none;
  display: block;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .peer-gallery-iframe {
    height: 400px;
  }
}
</style>
